<template>
    <div class="guide-element-rule">
        <resource-select
            :value="selectedGuide"
            :options="guideOptions"
            :loading="loadingResources"
            field="guideId"
            :read-only="readOnly"
            :check-valid-rule="checkValidRule"
            :placeholder="$t('guideElementRule.Select Guide')"
            @input="onGuideSelect">
            <template #noData>
                <div class="no-data-message">
                    {{ noDataText }}
                </div>
            </template>
        </resource-select>
        <template v-if="selectedGuide">
            <pendo-multiselect
                v-if="guideStepOptions.length > 1"
                :value="selectedGuideStep"
                full-width
                :append-to-body="false"
                :disabled="readOnly"
                :allow-empty="false"
                :options="guideStepOptions"
                label-key="name"
                value-key="guideStepId"
                :placeholder="$t('guideElementRule.Select Guide Step')"
                @select="onStepSelect" />
            <pendo-multiselect
                v-if="selectedGuideStep"
                :value="selectedGuideElement"
                full-width
                :append-to-body="false"
                :disabled="readOnly"
                :extra-label-width="90"
                :allow-empty="false"
                :options="guideElementOptions"
                :loading="loading"
                label-key="name"
                value-key="guideElementId"
                :placeholder="$t('guideElementRule.Select Guide Element')"
                @select="onElementSelect">
                <template #noData>
                    <div class="no-data-message">
                        {{ $t('guideElementRule.No guide elements found. Please select a different step or guide.') }}
                    </div>
                </template>
                <template #selectedLabel="{ option }">
                    <pendo-icon-option :option="option" />
                </template>
                <template #option="{ option }">
                    <pendo-icon-option :option="option" />
                </template>
            </pendo-multiselect>
            <template v-if="selectedGuideElement">
                <template v-if="!isLocaleEnglish">
                    <div class="flex-break" />
                    <strong>{{ $t('Condition') }}</strong>
                    <div class="flex-break" />
                </template>
                <fact-select
                    :options="factOptions"
                    :value="factValue"
                    :read-only="readOnly"
                    :check-valid-rule="checkValidRule"
                    @input="$emit('input', $event)" />
                <template v-if="rule.schema === 'guideElement'">
                    <template v-if="!isLocaleEnglish">
                        <div class="flex-break" />
                        <strong>{{ $t('Criteria') }}</strong>
                        <div class="flex-break" />
                    </template>
                    <simple-select
                        :options="clickedOptions"
                        :value="clickedValue"
                        preselect-first
                        field="clicked"
                        :disabled="readOnly"
                        :check-valid-rule="checkValidRule"
                        @input="$emit('input', $event)" />
                    <component
                        :is="valueComponent.component"
                        v-if="valueComponent"
                        v-bind="valueComponent.props"
                        :read-only="readOnly"
                        :check-valid-rule="checkValidRule"
                        @input="$emit('input', $event)" />
                </template>
                <template v-if="rule.schema === 'aggregation'">
                    <aggregation-rule
                        :rule="rule"
                        :read-only="readOnly"
                        :check-valid-rule="checkValidRule"
                        :is-locale-english="isLocaleEnglish"
                        @input="$emit('input', $event)" />
                </template>
            </template>
        </template>
    </div>
</template>

<script>
import get from 'lodash/get';
import { PendoMultiselect, PendoIconOption } from '@pendo/components';
import { sanitizeState } from '@pendo/services/Formatters';
import AggregationRule from '@/segments/components/segment-editor-vue/aggregation-rule.vue';
import ResourceSelect from '@/segments/components/segment-editor-vue/components/resource-select.vue';
import FactSelect from '@/segments/components/segment-editor-vue/components/fact-select.vue';
import NumberInput from '@/segments/components/segment-editor-vue/components/number-input.vue';
import RelativeDateRange from '@/segments/components/segment-editor-vue/components/relative-date-range.vue';
import SingleDate from '@/segments/components/segment-editor-vue/components/single-date.vue';
import SimpleSelect from '@/segments/components/segment-editor-vue/components/simple-select.vue';
import {
    getFactValue,
    getFactOptions,
    formatFactOptions,
    getNumberConfig,
    getRelativeDateRangeConfig,
    getSingleDateConfig,
} from '@/segments/components/segment-editor-vue/segment-editor.utils';
import { getNoResourcesFoundText } from '@/segments/components/segment-editor-vue/segment-editor.utils';

import { LOADING_STATES } from '@/constants';
import { connectAggregationToState, withStore, withComponent } from '@pendo/agg-connect';
import { getContent } from '@/guides/helpers/common.util';
import GuideElementsActivityAgg from '@/guides/aggregations/guide-elements-activity.aggregation';
import { processGuideElementsAggDropdown } from '@/guides/p2-guide-details/guide-metrics/guide-elements-table/utils';
import { getCurrentElementData } from '@pendo/one-ui-shared';

export default {
    name: 'GuideElementRule',
    components: {
        AggregationRule,
        FactSelect,
        ResourceSelect,
        PendoIconOption,
        PendoMultiselect,
        SingleDate,
        NumberInput,
        RelativeDateRange,
        SimpleSelect,
    },
    props: {
        rule: {
            type: Object,
            required: true,
        },
        config: {
            type: Object,
            required: true,
        },
        resources: {
            type: Array,
            required: true,
        },
        loadingResources: {
            type: Boolean,
            default: false,
        },
        checkValidRule: {
            type: Boolean,
            default: false,
        },
        isMultiApp: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        isLocaleEnglish: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['input'],
    data () {
        return {
            currentIds: undefined,
            currentElementData: [],
            elementsActivityAggStatus: null,
            guide: {},
            guideElements: [],
        };
    },
    computed: {
        noDataText () {
            return getNoResourcesFoundText({ rule: this.rule, isMultiApp: this.isMultiApp, resourceType: 'guide' });
        },
        guideOptions () {
            const guideOptions = this.resources
                .filter((guide) => {
                    const isResourceCenter = get(guide, 'attributes.resourceCenter') && guide.state !== 'public';
                    const isP2Guide = get(guide, 'attributes.type') === 'building-block';
                    const isEmail = get(guide, 'attributes.isEmail');

                    return !isResourceCenter && isP2Guide && !isEmail;
                })
                .map((guide) => {
                    return {
                        guideId: guide.id,
                        name: guide.name,
                        state: sanitizeState(guide).state,
                    };
                });

            if (
                this.rule.guideId &&
                this.rule.guideElementId &&
                !guideOptions.find((guide) => guide.guideId === this.rule.guideId)
            ) {
                guideOptions.push({
                    guideId: this.rule.guideId,
                    name: this.$t('guideElementRule.Deleted Guide ({guideId})', { guideId: this.rule.guideId }),
                    state: 'deleted',
                });
            }

            return guideOptions;
        },
        guideStepOptions () {
            const guideStepOptions = get(this.guide, 'steps', []).map((step, index) => {
                return {
                    guideStepId: step.id,
                    name: step.name
                        ? this.$t('guideElementRule.{stepName} (Step {stepIndex})', {
                            stepName: step.name,
                            stepIndex: index + 1,
                        })
                        : this.$t('guideElementRule.Step {stepIndex}', { stepIndex: index + 1 }),
                };
            });

            if (
                this.rule.guideStepId &&
                !guideStepOptions.find((guideStep) => guideStep.guideStepId === this.rule.guideStepId)
            ) {
                guideStepOptions.push({
                    guideStepId: this.rule.guideStepId,
                    name: this.$t('guideElementRule.Deleted Step ({guideStepId})', {
                        guideStepId: this.rule.guideStepId,
                    }),
                });
            }

            return guideStepOptions;
        },
        guideElementOptions () {
            return this.filterElementsByStep(this.rule.guideStepId);
        },
        selectedGuide () {
            return this.guideOptions.find((guide) => guide.guideId === this.rule.guideId);
        },
        selectedGuideStep () {
            return this.guideStepOptions.find((step) => step.guideStepId === this.rule.guideStepId);
        },
        selectedGuideElement () {
            return this.guideElements.find((guideElement) => guideElement.guideElementId === this.rule.guideElementId);
        },
        factOptions () {
            const options = getFactOptions({
                id: this.rule.id,
                onlyAllowGuideCompatibleRules: this.config.onlyAllowGuideCompatibleRules,
            });

            return formatFactOptions(options);
        },
        factValue () {
            return getFactValue(this.rule, this.factOptions);
        },
        loading () {
            return this.elementsActivityAggStatus === 'loading';
        },
        clickedOptions () {
            const options = [
                { clicked: 'ever', name: this.$t('guideElementRule.ever') },
                { clicked: 'since', name: this.$t('guideElementRule.since') },
                { clicked: 'withinLast', name: this.$t('guideElementRule.within last') },
            ];

            if (this.rule.fact === 'clicked') {
                options.push(
                    { clicked: 'atleast', name: this.$t('guideElementRule.at least') },
                    { clicked: 'atmost', name: this.$t('guideElementRule.at most') },
                );
            }

            return options;
        },
        clickedValue () {
            return this.clickedOptions.find((option) => option.clicked === this.rule.clicked);
        },
        valueComponent () {
            switch (this.rule.clicked) {
                case 'withinLast':
                    return getRelativeDateRangeConfig(this.rule);
                case 'since':
                    return getSingleDateConfig(this.rule, 'value');
                case 'atmost':
                case 'atleast': {
                    const component = getNumberConfig(this.rule, 'value');
                    component.props.labels = { suffix: this.$t('guideElementRule.times') };

                    return component;
                }
                default:
                    return false;
            }
        },
    },
    watch: {
        selectedGuide: {
            immediate: true,
            async handler (newGuide) {
                if (!newGuide) return;

                this.guide = this.resources.find((guide) => guide.id === newGuide.guideId);
                if (!this.guide) {
                    this.guide = { id: newGuide.guideId };
                    this.currentIds = [];

                    return;
                }

                const { steps } = await getContent(this.guide);

                if (steps.length === 1 && this.rule.guideStepId !== steps[0].id) {
                    this.onStepSelect({ guideStepId: steps[0].id });
                }

                this.currentElementData = steps.reduce((acc, step) => {
                    const stepElementData = getCurrentElementData(JSON.parse(step.dom)).map((element) => {
                        return {
                            ...element,
                            guideStepId: step.id,
                        };
                    });
                    acc.push(...stepElementData);

                    return acc;
                }, []);
                this.currentIds = this.currentElementData.map((element) => element.uiElementId);
            },
        },
    },
    created () {
        this.createGuideElementsActivityAgg$();
    },
    methods: {
        createGuideElementsActivityAgg$ () {
            this.elementsActivityAggStatus = LOADING_STATES.LOADING;
            const sourcesOfState = {
                component: this,
                store: this.$store,
            };
            const elementsStream$ = connectAggregationToState(
                GuideElementsActivityAgg,
                withStore({
                    blacklist: (state, getters) => getters['globalFilters/segmentBlacklist'],
                    includeAnonymous: (state, getters) => getters['globalFilters/segmentIncludeAnonymous'],
                    timeSeries: (state, getters) => getters['globalFilters/timeSeriesSpec'],
                }),
                withComponent({
                    guideId: 'guide.id',
                    currentIds: 'currentIds',
                }),
            )(sourcesOfState);

            elementsStream$.subscribe(({ status, value }) => {
                const { LOADING, RESOLVED, REJECTED, EMPTY } = LOADING_STATES;
                switch (status) {
                    case REJECTED:
                    case LOADING: {
                        this.elementsActivityAggStatus = status;
                        this.guideElements = [];
                        break;
                    }
                    case RESOLVED: {
                        const elementsActivity = processGuideElementsAggDropdown(value, this.currentElementData);

                        if (
                            this.rule.guideElementId &&
                            !elementsActivity.find((element) => element.guideElementId === this.rule.guideElementId)
                        ) {
                            elementsActivity.push({
                                guideElementId: this.rule.guideElementId,
                                guideStepId: this.rule.guideStepId,
                                name: this.$t('guideElementRule.Deleted Element ({guideElementId})', {
                                    guideElementId: this.rule.guideElementId,
                                }),
                            });
                        }

                        this.elementsActivityAggStatus = elementsActivity.length ? RESOLVED : EMPTY;
                        this.guideElements = elementsActivity;
                        break;
                    }
                }
            });
        },
        filterElementsByStep (stepId) {
            if (!stepId || stepId === '*') {
                return this.guideElements;
            }

            return this.guideElements.filter((element) => element.guideStepId === stepId);
        },
        onGuideSelect (guide) {
            const field = ['guideId', 'guideStepId', 'guideElementId'];
            const value = { guideId: guide.value.guideId, guideStepId: null, guideElementId: null };

            this.$emit('input', { field, value });
        },
        // If the aggregation has not yet resolved, onStepSelect is guaranteed to clear the guideElementId.
        onStepSelect (step) {
            const filteredElements = this.filterElementsByStep(step.guideStepId);
            const element = filteredElements.find(
                (guideElement) => guideElement.guideElementId === this.rule.guideElementId,
            );

            const field = ['guideStepId'];
            const value = { guideStepId: step.guideStepId };

            if (!element) {
                field.push('guideElementId');
                value.guideElementId = null;
            }

            this.$emit('input', { field, value });
        },
        onElementSelect (element) {
            this.$emit('input', { field: 'guideElementId', value: element.guideElementId });
        },
    },
};
</script>

<style scoped lang="less">
.flex-break {
    /* Reliable flex line break - forces next items to new line */
    flex-basis: 100%;
    height: 0;
    width: 0;
    overflow: hidden;
}
</style>
